import { createContext, useContext, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { db, tref } from '../lib/firebase.js'
import { onValue, ref } from 'firebase/database'
import { createQualifyingTeam, submitQualifyingResult } from '../lib/data.js'
import {
  createQualifyingEventTeam,
  submitQualifyingEventResult,
  watchQualifyingEvent,
} from '../lib/qualifyingEvents.js'
import { DEFAULT_PARS, parsFor, totalPar } from '../lib/scoring.js'
import CLUB from '../config/club.js'
import PageNav from '../components/PageNav.jsx'
import { scoreColor } from './scoreColors'

const PBCC_GREEN = CLUB.primaryColor
const PBCC_GOLD = CLUB.accentColor

// ── Light/dark theme (mirrors ScoreApp; same localStorage key) ──────────────
// Light: white-ish bg, dark-green text, green buttons. Dark: black bg, green
// buttons, white text. Every qualifying sub-component reads tokens from
// QualThemeCtx so the toggle flips the whole screen at once.
const QUAL_THEMES = {
  light: {
    name: 'light',
    pageBg: '#FFFFFF',
    text: PBCC_GREEN,
    textDim: 'rgba(var(--ge-primary-rgb),0.65)',
    greenText: PBCC_GREEN,
    panel: '#FFFFFF',
    panelBorder: 'rgba(var(--ge-primary-rgb),0.15)',
    cardShadow: '0 4px 14px rgba(0,0,0,0.08)',
    headerBg: '#FFFFFF',
    headerText: PBCC_GREEN,
    headerAccent: PBCC_GREEN,
    headerSub: '#6B7280',
    navArrowBg: PBCC_GREEN,
    navArrowText: '#FFFFFF',
    boxBg: 'transparent',
    boxBorder: PBCC_GREEN,
    boxText: PBCC_GREEN,
    selBg: PBCC_GREEN,
    selText: '#FFFFFF',
    saveBg: PBCC_GREEN,
    saveText: '#FFFFFF',
    inputBg: '#FFFFFF',
    inputText: '#111111',
    inputBorder: PBCC_GREEN,
    gridFilledBg: 'rgba(var(--ge-primary-rgb),0.10)',
    gridIdleBg: 'rgba(var(--ge-primary-rgb),0.04)',
    gridIdleText: 'rgba(var(--ge-primary-rgb),0.45)',
    toggleBg: '#FFFFFF',
    toggleText: PBCC_GREEN,
    toggleBorder: PBCC_GREEN,
  },
  dark: {
    name: 'dark',
    pageBg: '#000000',
    text: '#FFFFFF',
    textDim: 'rgba(255,255,255,0.70)',
    greenText: '#16A34A',
    panel: '#111111',
    panelBorder: 'rgba(255,255,255,0.10)',
    cardShadow: 'none',
    headerBg: 'transparent',
    headerText: '#FFFFFF',
    headerAccent: '#16A34A',
    headerSub: 'rgba(255,255,255,0.70)',
    navArrowBg: '#16A34A',
    navArrowText: '#FFFFFF',
    boxBg: '#16A34A',
    boxBorder: '#16A34A',
    boxText: '#FFFFFF',
    selBg: '#FFFFFF',
    selText: 'var(--ge-primary)',
    saveBg: '#16A34A',
    saveText: '#FFFFFF',
    inputBg: '#FFFFFF',
    inputText: '#111111',
    inputBorder: 'rgba(255,255,255,0.20)',
    gridFilledBg: 'rgba(255,255,255,0.10)',
    gridIdleBg: 'rgba(255,255,255,0.06)',
    gridIdleText: 'rgba(255,255,255,0.40)',
    toggleBg: '#111111',
    toggleText: '#FFFFFF',
    toggleBorder: '#16A34A',
  },
}

const QualThemeCtx = createContext(QUAL_THEMES.light)
const useQualTheme = () => useContext(QualThemeCtx)

// Theme state + toggle, shared by both qualifying sub-apps. Uses its OWN
// localStorage key 'qualifyTheme' (independent of the main scorecard's
// 'scorecardTheme') and DEFAULTS TO LIGHT — so the qualifying flow always
// opens in the light design regardless of the scorecard's theme. Only an
// explicit 'dark' value (set by toggling here) switches it to dark.
function useThemeState() {
  // ALWAYS start in light on every fresh load — never read a persisted 'dark'
  // value. The toggle still switches to dark during the session, but a reload
  // (or a fresh QR scan) always returns to light.
  const [theme, setTheme] = useState('light')
  const toggle = () => setTheme((cur) => {
    const next = cur === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem('qualifyTheme', next) } catch {}
    return next
  })
  return { theme, T: QUAL_THEMES[theme] || QUAL_THEMES.light, toggle }
}

function ThemeToggle({ theme, onToggle }) {
  const T = useQualTheme()
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full py-2 rounded-xl font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98] flex items-center justify-center gap-2 mb-3"
      style={{ backgroundColor: T.toggleBg, color: T.toggleText, border: `2px solid ${T.toggleBorder}` }}>
      {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
    </button>
  )
}

// Pre-round welcome screen for the qualifying scorecard. Mirrors ScoreApp's
// welcome: spinning logo (4.5s), Cinzel text fading in after it lands, then a
// big green START button that drops the player into check-in. Theme-aware.
function QualifyingWelcome({ tournamentName, onStart }) {
  const T = useQualTheme()
  useEffect(() => {
    const id = 'cinzel-welcome-font'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;800&display=swap'
    document.head.appendChild(link)
  }, [])
  return (
    <div
      className="flex flex-col items-center justify-start text-center px-6 pt-10 pb-10"
      style={{ backgroundColor: '#FFFFFF', minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @keyframes qwLogoIn {
          0%   { opacity: 0; transform: scale(0) rotate(0deg); }
          100% { opacity: 1; transform: scale(1) rotate(1080deg); }
        }
        @keyframes qwFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes qwBounceIn {
          0%   { opacity: 0; transform: scale(0.8); }
          60%  { opacity: 1; transform: scale(1.06); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <img
        src={CLUB.logo}
        alt=""
        style={{
          width: '300px', height: '300px', objectFit: 'contain', marginBottom: 12,
          animation: 'qwLogoIn 4.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both',
        }}
      />
      {tournamentName ? (
        <div
          className="text-lg font-bold uppercase tracking-wide"
          style={{ fontFamily: 'Cinzel, serif', color: T.textDim, animation: 'qwFadeUp 0.5s ease 4s backwards' }}>
          {tournamentName}
        </div>
      ) : null}
      <div
        className="text-3xl font-extrabold uppercase mt-2 leading-tight"
        style={{ fontFamily: 'Cinzel, serif', color: T.greenText, animation: 'qwFadeUp 0.5s ease 4.5s backwards' }}>
        Welcome to your qualifying round
      </div>
      <div
        className="text-5xl font-extrabold uppercase mt-6 leading-tight"
        style={{ fontFamily: 'Cinzel, serif', color: '#8B1A1A', animation: 'qwFadeUp 0.5s ease 5s backwards' }}>
        Teeing off on Hole #1
      </div>
      <button
        type="button"
        onClick={onStart}
        className="mt-10 w-full max-w-xs py-4 rounded-2xl font-extrabold text-lg uppercase tracking-widest transition active:scale-[0.98]"
        style={{
          fontFamily: 'Cinzel, serif',
          background: T.saveBg,
          color: T.saveText,
          boxShadow: '0 6px 18px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.25)',
          animation: 'qwBounceIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 5.5s backwards',
        }}>
        🏌️ Start Qualifying Round →
      </button>
    </div>
  )
}

// Pro-shop self-register flow:
//   /qualify?t={tournamentId} — legacy, writes to a tournament's
//     /qualifying child node (kept verbatim for backward compatibility).
//   /qualify?q={eventId} — standalone Qualifying Event, writes to
//     /qualifyingEvents/{eventId}/teams/{teamId}. NO tournament exists.
//
// The default export routes between the two implementations based on
// which query param is present. Pre-existing posters / saved links keep
// working unchanged.

export default function QualifyApp() {
  const [params] = useSearchParams()
  const eventId = params.get('q')
  const tournamentId = params.get('t')
  if (eventId) return <EventQualifyApp eventId={eventId} />
  if (tournamentId) return <TournamentQualifyApp tournamentId={tournamentId} />
  return (
    <Centered>
      <Card>
        Missing event or tournament. Scan the qualifying QR code from the pro shop.
      </Card>
    </Centered>
  )
}

function TournamentQualifyApp({ tournamentId }) {

  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')
  const [step, setStep] = useState('checkin') // checkin → score → manual → done
  const [active, setActive] = useState(null) // { teamId, name, players }
  const [scores, setScores] = useState({})
  const [hole, setHole] = useState(1)
  const [submittedTotal, setSubmittedTotal] = useState(null)
  const [welcomeDone, setWelcomeDone] = useState(false)
  const [draft, setDraft] = useState(null) // last-entered check-in values, restored on Back
  const [manualBusy, setManualBusy] = useState(false)
  const [resumed, setResumed] = useState(false) // restored an in-progress round
  const navigate = useNavigate()
  const { theme, T, toggle } = useThemeState()

  // Submit a final total only (no hole-by-hole card).
  const submitManualTotal = async (total) => {
    if (!active?.teamId) return
    setManualBusy(true)
    try {
      await submitQualifyingResult(tournamentId, active.teamId, { score: total, holes: null, teamName: active.name })
      try {
        localStorage.removeItem(`qualify_${tournamentId}_${active.teamId}`)
        localStorage.removeItem(`qualify_active_${tournamentId}`)
      } catch {}
      setSubmittedTotal(total)
      setStep('done')
    } catch (e) {
      setError('Could not submit. Check your signal and try again.')
    }
    setManualBusy(false)
  }
  // Explicit "save & finish later" — scores are already persisted; just leave to
  // the tournament hub. Re-opening /qualify restores this round automatically.
  const saveAndExit = () => navigate('/live')

  // Subscribe to tournament for header info (name, course, pars).
  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the qualifying QR code from the pro shop.')
      return
    }
    const off = onValue(tref(`tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  // Restore an in-progress session if the page was refreshed mid-round.
  // Keyed by tournament so multiple tournaments don't collide.
  useEffect(() => {
    if (!tournamentId) return
    try {
      const raw = localStorage.getItem(`qualify_active_${tournamentId}`)
      if (!raw) return
      const sess = JSON.parse(raw)
      if (!sess?.teamId) return
      setActive(sess)
      const savedScores = localStorage.getItem(`qualify_${tournamentId}_${sess.teamId}`)
      if (savedScores) {
        try { const parsed = JSON.parse(savedScores) || {}; setScores(parsed); if (Object.keys(parsed).length > 0) setResumed(true) } catch {}
      }
      setStep('score')
    } catch {}
  }, [tournamentId])

  // Persist scores so a refresh doesn't lose progress.
  useEffect(() => {
    if (!tournamentId || !active?.teamId || step !== 'score') return
    try {
      localStorage.setItem(`qualify_${tournamentId}_${active.teamId}`, JSON.stringify(scores))
    } catch {}
  }, [scores, active, tournamentId, step])

  // Auto-submit the moment all 18 holes are scored.
  useEffect(() => {
    if (step !== 'score' || !active?.teamId) return
    const allFilled = Array.from({ length: 18 }, (_, i) => Number(scores[i + 1]) > 0).every(Boolean)
    if (!allFilled) return
    let cancelled = false
    ;(async () => {
      const total = Object.values(scores).reduce((a, b) => a + (Number(b) || 0), 0)
      try {
        await submitQualifyingResult(tournamentId, active.teamId, {
          score: total,
          holes: scores,
          teamName: active.name,
        })
        try {
          localStorage.removeItem(`qualify_${tournamentId}_${active.teamId}`)
          localStorage.removeItem(`qualify_active_${tournamentId}`)
        } catch {}
      } catch (e) {
        if (!cancelled) setError('Could not submit. Check your signal and try again.')
        return
      }
      if (cancelled) return
      setSubmittedTotal(total)
      setStep('done')
    })()
    return () => { cancelled = true }
  }, [scores, step, active, tournamentId])

  if (error) return <Centered><Card>{error}</Card></Centered>
  if (!tournament) return <Centered>Loading…</Centered>

  const info = tournament.info || {}
  const pars = parsFor(tournament)
  const tPar = totalPar(pars)

  // Admin-flipped manual close (Close Qualifying button on the
  // QualifyingTab). Takes precedence over the dated window — once the
  // admin closes qualifying, the QR scorecard is locked even if the
  // window's end date hasn't passed yet.
  if (info.qualifyingClosed) {
    return <Centered><Card><p className="text-center font-bold" style={{ color: PBCC_GREEN }}>Qualifying is closed.</p></Card></Centered>
  }
  // Optional admin-set window (skip for self-register since pro-shop manages access).
  const opens = info.qualifyingOpens ? new Date(info.qualifyingOpens) : null
  const closes = info.qualifyingCloses ? new Date(info.qualifyingCloses) : null
  const now = new Date()
  if (opens && now < opens) {
    return <Centered><Card><p className="text-center font-bold" style={{ color: PBCC_GREEN }}>Qualifying opens {opens.toLocaleDateString()}.</p></Card></Centered>
  }
  if (closes && now > new Date(closes.getTime() + 24 * 60 * 60 * 1000)) {
    return <Centered><Card><p className="text-center font-bold" style={{ color: PBCC_GREEN }}>Qualifying window has closed.</p></Card></Centered>
  }

  if (step === 'checkin' && !welcomeDone) {
    return (
      <QualThemeCtx.Provider value={T}>
        <QualifyingWelcome tournamentName={info.name} onStart={() => setWelcomeDone(true)} />
      </QualThemeCtx.Provider>
    )
  }

  return (
    <QualThemeCtx.Provider value={T}>
    <div className="min-h-full relative" style={{ backgroundColor: T.pageBg }}>
      {/* Scoring screen: Back returns to the check-in step (so a team can fix a
          name typo) and there is NO Home button — a qualifying player must
          never be sent to the program login/home screen. Check-in step keeps
          its normal PageNav. */}
      {step === 'score' ? (
        <div className="absolute top-3 left-3 flex gap-2 z-30">
          <button
            onClick={() => setStep('checkin')}
            className="rounded-lg px-2.5 py-1.5 text-[11px] font-extrabold uppercase tracking-wider transition active:scale-95 whitespace-nowrap"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)', color: PBCC_GOLD, border: `1px solid ${PBCC_GOLD}66` }}>
            ← Back
          </button>
        </div>
      ) : (step === 'checkin' || step === 'manual') ? null : (
        <PageNav back homeTo="/live" />
      )}
      {/* Dark-green header band (light theme); transparent on the black dark theme. */}
      <div style={{ backgroundColor: T.headerBg }}>
        <header className="max-w-md mx-auto text-center px-5 pt-12 pb-5">
          <p className="text-base font-extrabold uppercase tracking-widest"
             style={{ color: T.headerAccent, letterSpacing: '0.18em' }}>
            {step === 'checkin' ? 'Qualifying Check-In' : 'Qualifying Round'}
          </p>
          <h1 className="font-extrabold mt-1" style={{ color: T.headerText, fontSize: T.name === 'light' ? '1.65rem' : '1.25rem' }}>{info.name}</h1>
          <p className="text-xs" style={{ color: T.headerSub }}>{info.course} · Par {tPar}</p>
          {active && step !== 'checkin' ? (
            <p className="mt-2 text-base font-extrabold" style={{ color: T.headerText }}>{active.name}</p>
          ) : null}
        </header>
      </div>
      <div className="max-w-md mx-auto p-5">
        <ThemeToggle theme={theme} onToggle={toggle} />
        {step === 'checkin' && (
          <CheckInForm
            initial={draft}
            onSubmit={async (form) => {
              setDraft(form)
              const players = [
                [form.p1First, form.p1Last].filter(Boolean).join(' ').trim(),
                [form.p2First, form.p2Last].filter(Boolean).join(' ').trim(),
              ].filter(Boolean)
              try {
                const teamId = await createQualifyingTeam(tournamentId, {
                  teamName: form.teamName,
                  player1: players[0] || '',
                  player2: players[1] || '',
                })
                const sess = { teamId, name: form.teamName, players }
                setActive(sess)
                try {
                  localStorage.setItem(`qualify_active_${tournamentId}`, JSON.stringify(sess))
                } catch {}
                setHole(1)
                setScores({})
                setStep('score')
              } catch (e) {
                setError('Could not check in. Check your signal and try again.')
              }
            }}
          />
        )}

        {step === 'score' && active && (
          <>
            {resumed ? (
              <div className="max-w-md mx-auto px-4 pt-2">
                <div style={{ background: `${PBCC_GOLD}22`, border: `1px solid ${PBCC_GOLD}66`, color: T.text, borderRadius: 12, padding: '8px 12px', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                  ↩︎ Resumed your saved round — pick up where you left off.
                </div>
              </div>
            ) : null}
            <ScorePanel
              pars={pars}
              hole={hole}
              setHole={setHole}
              scores={scores}
              setScores={setScores}
            />
            <ScoreFooter onFinalTotal={() => setStep('manual')} onSaveExit={saveAndExit} />
          </>
        )}

        {step === 'manual' && active && (
          <FinalTotalEntry pars={pars} busy={manualBusy} onSubmit={submitManualTotal} onCancel={() => setStep('score')} />
        )}

        {step === 'done' && active && (
          <ThankYouCard teamName={active.name} total={submittedTotal} tPar={tPar} />
        )}
      </div>
    </div>
    </QualThemeCtx.Provider>
  )
}

// ── Standalone Qualifying Event flow (?q=eventId) ────────────────────────
// Mirrors TournamentQualifyApp but reads/writes /qualifyingEvents/{eventId}.
// No tournament exists yet, so pars come from CLUB.course (via DEFAULT_PARS)
// and the header shows the event name + format instead of tournament name +
// course. localStorage keys are namespaced 'qevent_' so two parallel
// flows (one tournament-qualifying, one event-qualifying) on the same
// phone don't collide.
function EventQualifyApp({ eventId }) {
  const [event, setEvent] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState('checkin')
  const [active, setActive] = useState(null)
  const [scores, setScores] = useState({})
  const [hole, setHole] = useState(1)
  const [submittedTotal, setSubmittedTotal] = useState(null)
  const [welcomeDone, setWelcomeDone] = useState(false)
  const [draft, setDraft] = useState(null) // last-entered check-in values, restored on Back
  const [manualBusy, setManualBusy] = useState(false)
  const [resumed, setResumed] = useState(false)
  const navigate = useNavigate()
  const { theme, T, toggle } = useThemeState()

  const submitManualTotal = async (total) => {
    if (!active?.teamId) return
    setManualBusy(true)
    try {
      await submitQualifyingEventResult(eventId, active.teamId, { score: total, holes: null, teamName: active.name })
      try {
        localStorage.removeItem(`qevent_${eventId}_${active.teamId}`)
        localStorage.removeItem(`qevent_active_${eventId}`)
      } catch {}
      setSubmittedTotal(total)
      setStep('done')
    } catch (e) {
      setError('Could not submit. Check your signal and try again.')
    }
    setManualBusy(false)
  }
  const saveAndExit = () => navigate('/live')

  useEffect(() => {
    if (!eventId) {
      setError('Missing event ID. Scan the qualifying QR code.')
      setLoaded(true)
      return
    }
    const off = watchQualifyingEvent(eventId, (v) => {
      if (!v) setError('Qualifying event not found.')
      else setEvent(v)
      setLoaded(true)
    })
    return () => off()
  }, [eventId])

  // Restore an in-progress session if the page was refreshed mid-round.
  useEffect(() => {
    if (!eventId) return
    try {
      const raw = localStorage.getItem(`qevent_active_${eventId}`)
      if (!raw) return
      const sess = JSON.parse(raw)
      if (!sess?.teamId) return
      setActive(sess)
      const savedScores = localStorage.getItem(`qevent_${eventId}_${sess.teamId}`)
      if (savedScores) {
        try { const parsed = JSON.parse(savedScores) || {}; setScores(parsed); if (Object.keys(parsed).length > 0) setResumed(true) } catch {}
      }
      setStep('score')
    } catch {}
  }, [eventId])

  // Persist scores so a refresh doesn't lose progress.
  useEffect(() => {
    if (!eventId || !active?.teamId || step !== 'score') return
    try {
      localStorage.setItem(`qevent_${eventId}_${active.teamId}`, JSON.stringify(scores))
    } catch {}
  }, [scores, active, eventId, step])

  // Auto-submit at hole 18.
  useEffect(() => {
    if (step !== 'score' || !active?.teamId) return
    const allFilled = Array.from({ length: 18 }, (_, i) => Number(scores[i + 1]) > 0).every(Boolean)
    if (!allFilled) return
    let cancelled = false
    ;(async () => {
      const total = Object.values(scores).reduce((a, b) => a + (Number(b) || 0), 0)
      try {
        await submitQualifyingEventResult(eventId, active.teamId, {
          score: total, holes: scores, teamName: active.name,
        })
        try {
          localStorage.removeItem(`qevent_${eventId}_${active.teamId}`)
          localStorage.removeItem(`qevent_active_${eventId}`)
        } catch {}
      } catch (e) {
        if (!cancelled) setError('Could not submit. Check your signal and try again.')
        return
      }
      if (cancelled) return
      setSubmittedTotal(total)
      setStep('done')
    })()
    return () => { cancelled = true }
  }, [scores, step, active, eventId])

  if (!loaded) return <Centered>Loading…</Centered>
  if (error) return <Centered><Card>{error}</Card></Centered>

  const info = event?.info || {}
  // Standalone events aren't tied to a tournament, so pars come straight
  // from the club's course config. Same fallback the tournament path uses
  // when info.pars is missing.
  const pars = DEFAULT_PARS
  const tPar = totalPar(pars)

  // Admin closed → players can't post new scores.
  if (info.status === 'closed') {
    return <Centered><Card><p className="text-center font-bold" style={{ color: PBCC_GREEN }}>Qualifying is closed.</p></Card></Centered>
  }

  if (step === 'checkin' && !welcomeDone) {
    return (
      <QualThemeCtx.Provider value={T}>
        <QualifyingWelcome tournamentName={info.name} onStart={() => setWelcomeDone(true)} />
      </QualThemeCtx.Provider>
    )
  }

  return (
    <QualThemeCtx.Provider value={T}>
    <div className="min-h-full relative" style={{ backgroundColor: T.pageBg }}>
      {/* Scoring screen: Back returns to the check-in step (so a team can fix a
          name typo) and there is NO Home button — a qualifying player must
          never be sent to the program login/home screen. Check-in step keeps
          its normal NavBar. */}
      {step === 'score' ? (
        <div className="flex items-center gap-2 px-4 pt-3">
          <button
            onClick={() => setStep('checkin')}
            style={{ color: T.text, background: 'transparent', border: `1px solid ${T.panelBorder}`, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 10px', borderRadius: 8 }}>
            ← Back
          </button>
        </div>
      ) : (step === 'checkin' || step === 'manual') ? null : (
        <NavBar />
      )}
      {/* Dark-green header band (light theme); transparent on the black dark theme. */}
      <div style={{ backgroundColor: T.headerBg }}>
        <header className="max-w-md mx-auto text-center px-5 pt-6 pb-5">
          <p className="text-base font-extrabold uppercase tracking-widest"
             style={{ color: T.headerAccent, letterSpacing: '0.18em' }}>
            {step === 'checkin' ? 'Qualifying Check-In' : 'Qualifying Round'}
          </p>
          <h1 className="font-extrabold mt-1" style={{ color: T.headerText, fontSize: T.name === 'light' ? '1.65rem' : '1.25rem' }}>{info.name}</h1>
          <p className="text-xs" style={{ color: T.headerSub }}>{info.format} · Par {tPar}</p>
          {active && step !== 'checkin' ? (
            <p className="mt-2 text-base font-extrabold" style={{ color: T.headerText }}>{active.name}</p>
          ) : null}
        </header>
      </div>
      <div className="max-w-md mx-auto p-5">
        <ThemeToggle theme={theme} onToggle={toggle} />
        {step === 'checkin' && (
          <CheckInForm
            initial={draft}
            onSubmit={async (form) => {
              setDraft(form)
              const players = [
                [form.p1First, form.p1Last].filter(Boolean).join(' ').trim(),
                [form.p2First, form.p2Last].filter(Boolean).join(' ').trim(),
              ].filter(Boolean)
              try {
                const teamId = await createQualifyingEventTeam(eventId, {
                  teamName: form.teamName,
                  player1: players[0] || '',
                  player2: players[1] || '',
                })
                const sess = { teamId, name: form.teamName, players }
                setActive(sess)
                try {
                  localStorage.setItem(`qevent_active_${eventId}`, JSON.stringify(sess))
                } catch {}
                setHole(1)
                setScores({})
                setStep('score')
              } catch (e) {
                setError('Could not check in. Check your signal and try again.')
              }
            }}
          />
        )}

        {step === 'score' && active && (
          <>
            {resumed ? (
              <div style={{ background: `${PBCC_GOLD}22`, border: `1px solid ${PBCC_GOLD}66`, color: T.text, borderRadius: 12, padding: '8px 12px', fontSize: 13, fontWeight: 600, textAlign: 'center', marginBottom: 10 }}>
                ↩︎ Resumed your saved round — pick up where you left off.
              </div>
            ) : null}
            <ScorePanel pars={pars} hole={hole} setHole={setHole} scores={scores} setScores={setScores} />
            <ScoreFooter onFinalTotal={() => setStep('manual')} onSaveExit={saveAndExit} />
          </>
        )}

        {step === 'manual' && active && (
          <FinalTotalEntry pars={pars} busy={manualBusy} onSubmit={submitManualTotal} onCancel={() => setStep('score')} />
        )}

        {step === 'done' && active && (
          <ThankYouCard teamName={active.name} total={submittedTotal} tPar={tPar} />
        )}
      </div>
    </div>
    </QualThemeCtx.Provider>
  )
}

// Back + Home bar shared by the event flow. Tournament flow keeps its
// existing PageNav back-only chrome for backward compatibility.
function NavBar() {
  const T = useQualTheme()
  const navigate = useNavigate()
  const linkStyle = {
    color: T.text,
    background: 'transparent',
    border: `1px solid ${T.panelBorder}`,
    textDecoration: 'none', cursor: 'pointer',
    fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
    padding: '6px 10px', borderRadius: 8,
  }
  // Home goes to the tournament hub (/live) — a player-safe home that never
  // bounces to the access-code screen. Back steps within history.
  const back = () => { if (window.history.length > 1) navigate(-1); else navigate('/live') }
  return (
    <div className="flex items-center gap-2 px-4 pt-3">
      <button onClick={back} style={linkStyle}>← Back</button>
      <Link to="/live" style={linkStyle}>🏠 Home</Link>
    </div>
  )
}

// ── Check-in form ─────────────────────────────────────────────────────────

function CheckInForm({ onSubmit, initial }) {
  const T = useQualTheme()
  const light = T.name === 'light'
  // Seed from `initial` so returning from the scoring screen (Back) keeps what
  // the team already typed instead of blanking the form. teamNameEdited starts
  // true when restoring so the auto-suggest effect doesn't overwrite the
  // restored team name.
  const [p1First, setP1First] = useState(initial?.p1First || '')
  const [p1Last, setP1Last] = useState(initial?.p1Last || '')
  const [p2First, setP2First] = useState(initial?.p2First || '')
  const [p2Last, setP2Last] = useState(initial?.p2Last || '')
  const [teamName, setTeamName] = useState(initial?.teamName || '')
  const [teamNameEdited, setTeamNameEdited] = useState(!!initial)
  const [busy, setBusy] = useState(false)

  // Auto-suggest team name as "LastName/LastName" while admin hasn't
  // overridden it manually.
  useEffect(() => {
    if (teamNameEdited) return
    if (p1Last && p2Last) {
      setTeamName(`${p1Last}/${p2Last}`)
    } else if (p1Last) {
      setTeamName(p1Last)
    } else {
      setTeamName('')
    }
  }, [p1Last, p2Last, teamNameEdited])

  const trimmed = (s) => s.trim()
  const ready =
    trimmed(p1First).length > 0 &&
    trimmed(p1Last).length > 0 &&
    trimmed(p2First).length > 0 &&
    trimmed(p2Last).length > 0 &&
    trimmed(teamName).length > 0

  const submit = async () => {
    if (!ready || busy) return
    setBusy(true)
    try {
      await onSubmit({
        p1First: trimmed(p1First),
        p1Last: trimmed(p1Last),
        p2First: trimmed(p2First),
        p2Last: trimmed(p2Last),
        teamName: trimmed(teamName),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <p className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--ge-primary)', marginBottom: light ? 6 : 12 }}>
        Enter your team
      </p>
      {light ? <div style={{ height: 3, width: 64, backgroundColor: PBCC_GREEN, borderRadius: 2, marginBottom: 14 }} /> : null}

      <Section label="Player 1">
        <NameRow
          first={p1First} setFirst={setP1First}
          last={p1Last} setLast={setP1Last}
        />
      </Section>

      <Section label="Player 2">
        <NameRow
          first={p2First} setFirst={setP2First}
          last={p2Last} setLast={setP2Last}
        />
      </Section>

      <Section label="Team Name">
        <div style={{ position: 'relative' }}>
          {light ? <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 15, opacity: 0.65, pointerEvents: 'none' }}>👤</span> : null}
          <input
            className="w-full rounded-xl py-3 text-base"
            style={{ backgroundColor: T.inputBg, color: light ? 'var(--ge-primary)' : T.inputText, fontWeight: light ? 700 : undefined, border: '2px solid var(--ge-primary)', paddingLeft: light ? 34 : 12, paddingRight: 12 }}
            placeholder="e.g. Smith/Jones"
            value={teamName}
            onChange={(e) => { setTeamName(e.target.value); setTeamNameEdited(true) }}
          />
        </div>
        <p className={`text-[11px] mt-1 ${light ? 'italic' : ''}`} style={{ color: light ? '#6B7280' : T.textDim }}>Auto-fills from your last names — feel free to change it.</p>
      </Section>

      <button
        onClick={submit}
        disabled={!ready || busy}
        className="w-full mt-2 py-4 rounded-2xl font-extrabold uppercase tracking-widest"
        style={{
          background: 'var(--ge-primary)',
          color: '#FFFFFF',
          boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
          opacity: ready ? 1 : 0.5,
        }}>
        {busy ? 'Checking in…' : (light ? '🏌️ Start Qualifying Round' : 'Start Qualifying Round')}
      </button>
    </Card>
  )
}

function Section({ label, children }) {
  const T = useQualTheme()
  return (
    <div className="mb-4">
      <label className="text-[11px] font-extrabold uppercase tracking-widest mb-1.5 block"
             style={{ color: 'var(--ge-primary)' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

function NameRow({ first, setFirst, last, setLast }) {
  const T = useQualTheme()
  const light = T.name === 'light'
  const inputStyle = {
    backgroundColor: T.inputBg, color: light ? 'var(--ge-primary)' : T.inputText, fontWeight: light ? 700 : undefined,
    border: '2px solid var(--ge-primary)',
    paddingLeft: light ? 34 : 12, paddingRight: 12,
  }
  const PersonIcon = () => light
    ? <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 15, opacity: 0.65, pointerEvents: 'none' }}>👤</span>
    : null
  return (
    <div className="grid grid-cols-2 gap-2">
      <div style={{ position: 'relative' }}>
        <PersonIcon />
        <input
          className="w-full rounded-xl py-3 text-base"
          style={inputStyle}
          placeholder="First name"
          autoCapitalize="words"
          value={first}
          onChange={(e) => setFirst(e.target.value)}
        />
      </div>
      <div style={{ position: 'relative' }}>
        <PersonIcon />
        <input
          className="w-full rounded-xl py-3 text-base"
          style={inputStyle}
          placeholder="Last name"
          autoCapitalize="words"
          value={last}
          onChange={(e) => setLast(e.target.value)}
        />
      </div>
    </div>
  )
}

// ── Score panel ──────────────────────────────────────────────────────────

// Below the scorecard: a always-available "enter final total instead" path and
// an explicit "save & finish later" exit (scores auto-save, so nothing is lost).
function ScoreFooter({ onFinalTotal, onSaveExit }) {
  const T = useQualTheme()
  const btn = { width: '100%', marginTop: 10, padding: '13px', borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: 'pointer', border: `1.5px solid ${T.panelBorder}`, background: 'transparent', color: T.text }
  return (
    <div className="max-w-md mx-auto px-4 pb-10">
      <button onClick={onFinalTotal} style={btn}>⛳ Enter final total instead →</button>
      <button onClick={onSaveExit} style={btn}>⛈ Rain Delay — pause &amp; resume later</button>
      <p style={{ textAlign: 'center', fontSize: 12, color: T.greenText, marginTop: 8 }}>
        Your progress saves automatically — close anytime and come back to finish where you left off.
      </p>
    </div>
  )
}

// Final-total-only entry (rain-shortened rounds / prefer not to go hole-by-hole).
function FinalTotalEntry({ pars, busy, onSubmit, onCancel }) {
  const T = useQualTheme()
  const [val, setVal] = useState('')
  const n = Number(val)
  const par = (pars || []).reduce((a, b) => a + (Number(b) || 0), 0)
  return (
    <div className="max-w-md mx-auto px-4 pt-4 pb-10">
      <p style={{ color: T.text, fontWeight: 800, fontSize: 17, marginBottom: 10, textAlign: 'center' }}>Enter your team's total for the round</p>
      <input
        inputMode="numeric" pattern="[0-9]*" autoFocus value={val}
        onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
        placeholder="00"
        style={{ width: '100%', textAlign: 'center', fontSize: 56, fontWeight: 800, color: T.inputText, background: T.inputBg, border: `2px solid ${T.inputBorder}`, borderRadius: 16, padding: '14px 0' }} />
      <p style={{ textAlign: 'center', color: T.greenText, fontSize: 13, margin: '8px 0 16px' }}>Par {par}</p>
      <button disabled={!(n > 0) || busy} onClick={() => onSubmit(n)}
        style={{ width: '100%', padding: '15px', borderRadius: 14, fontWeight: 800, fontSize: 17, border: 'none', background: T.saveBg, color: T.saveText, opacity: n > 0 && !busy ? 1 : 0.5, cursor: 'pointer' }}>
        {busy ? 'Submitting…' : 'Submit final total'}
      </button>
      <button onClick={onCancel} style={{ width: '100%', marginTop: 10, padding: '12px', background: 'transparent', border: 'none', color: T.greenText, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>← Back to scorecard</button>
    </div>
  )
}

function ScorePanel({ pars, hole, setHole, scores, setScores }) {
  const T = useQualTheme()
  const par = pars[hole - 1]
  const cur = scores[hole]
  // Fixed 1-8 on every hole so any score (including a hole-in-one) can be
  // entered on all 18 holes.
  const buttons = [1, 2, 3, 4, 5, 6, 7, 8]

  // Pre-select par on first visit so a missed tap doesn't leave the hole blank.
  useEffect(() => {
    setScores((s) => (s[hole] != null ? s : { ...s, [hole]: par }))
  }, [hole, par, setScores])

  const filledCount = Object.values(scores).filter((v) => Number(v) > 0).length
  const total = Object.values(scores).reduce((a, b) => a + (Number(b) || 0), 0)
  const allDone = filledCount >= 18

  // Save & Next: advances to the next unfilled hole; if everything's filled
  // the auto-submit useEffect on the parent will fire automatically.
  const advance = () => {
    for (let i = 1; i <= 18; i++) {
      const next = ((hole - 1 + i) % 18) + 1
      if (Number(scores[next]) <= 0 || scores[next] == null) {
        setHole(next)
        return
      }
    }
  }

  const onLastHole = hole === 18

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-center justify-between">
          <button
            onClick={() => setHole((h) => (h <= 1 ? 18 : h - 1))}
            className="text-3xl font-bold w-10 h-10 rounded-full flex items-center justify-center"
            style={{ color: T.navArrowText, backgroundColor: T.navArrowBg }}>‹</button>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest" style={{ color: T.greenText }}>Hole</div>
            <div className="text-5xl font-extrabold leading-none" style={{ color: T.text }}>{hole}</div>
            <div className="text-xs mt-1" style={{ color: T.greenText }}>Par {par}</div>
          </div>
          <button
            onClick={() => setHole((h) => (h >= 18 ? 1 : h + 1))}
            className="text-3xl font-bold w-10 h-10 rounded-full flex items-center justify-center"
            style={{ color: T.navArrowText, backgroundColor: T.navArrowBg }}>›</button>
        </div>

        <div
          className="mt-4 grid gap-2"
          style={{ gridTemplateColumns: `repeat(${buttons.length}, minmax(0, 1fr))` }}>
          {buttons.map((n) => {
            const selected = cur === n
            const sc = selected ? scoreColor(n, par) : null // null at par → button unchanged
            return (
              <button
                key={n}
                onClick={() => setScores((s) => ({ ...s, [hole]: n }))}
                className="py-3 rounded-xl text-base font-extrabold transition active:scale-[0.96]"
                style={
                  selected
                    ? { backgroundColor: sc ?? T.selBg, color: sc ? '#FFFFFF' : T.selText, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }
                    : { backgroundColor: T.boxBg, color: T.boxText, border: `1.5px solid ${T.boxBorder}` }
                }>
                {n}
              </button>
            )
          })}
        </div>

        <button
          onClick={allDone && onLastHole ? undefined : advance}
          disabled={allDone && onLastHole}
          className="w-full mt-4 py-3 rounded-2xl font-extrabold uppercase tracking-widest text-sm"
          style={{ background: T.saveBg, color: T.saveText, opacity: allDone && onLastHole ? 0.6 : 1 }}>
          {allDone ? 'Submitting…' : (onLastHole ? 'Save Hole 18' : 'Save & Next ▶')}
        </button>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-widest" style={{ color: T.greenText }}>Running Total</span>
          <span className="text-base font-extrabold" style={{ color: T.text }}>
            {total} through {filledCount} hole{filledCount === 1 ? '' : 's'}
          </span>
        </div>
        <HoleGrid scores={scores} hole={hole} setHole={setHole} />
      </Card>
    </div>
  )
}

function HoleGrid({ scores, hole, setHole }) {
  const T = useQualTheme()
  return (
    <div className="grid grid-cols-9 gap-1">
      {Array.from({ length: 18 }, (_, i) => {
        const h = i + 1
        const v = scores[h]
        const active = h === hole
        return (
          <button
            key={h}
            onClick={() => setHole(h)}
            className="rounded-md py-1.5 text-[11px] font-bold leading-tight"
            style={{
              backgroundColor: active ? T.selBg : v ? T.gridFilledBg : T.gridIdleBg,
              color: active ? T.selText : v ? T.text : T.gridIdleText,
              border: `1px solid ${T.panelBorder}`,
            }}>
            <div className="text-[9px] opacity-70">H{h}</div>
            <div>{v ?? '—'}</div>
          </button>
        )
      })}
    </div>
  )
}

function ThankYouCard({ teamName, total, tPar }) {
  const T = useQualTheme()
  const diff = (total ?? 0) - tPar
  const diffStr = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`
  return (
    <Card>
      <div className="text-6xl text-center mb-2">✅</div>
      <p className="text-center font-extrabold text-2xl" style={{ color: T.text }}>Qualifying Complete!</p>
      <p className="text-center text-sm mt-1" style={{ color: T.textDim }}>Team: {teamName}</p>
      <div className="mt-4 rounded-xl px-4 py-4 text-center"
           style={{ backgroundColor: T.gridFilledBg, border: `1px solid ${T.greenText}` }}>
        <p className="text-sm" style={{ color: T.greenText }}>Your score of</p>
        <p className="text-5xl font-extrabold mt-1" style={{ color: T.greenText }}>{total ?? '—'}</p>
        <p className="text-sm mt-1" style={{ color: T.greenText }}>{diffStr} vs Par · has been submitted.</p>
      </div>
      <p className="text-center text-base font-bold mt-5" style={{ color: T.text }}>Good luck in the tournament! 🍀</p>
    </Card>
  )
}

function Card({ children }) {
  const T = useQualTheme()
  return (
    <div className="rounded-2xl p-4"
         style={{ backgroundColor: T.panel, border: `1px solid ${T.panelBorder}`, boxShadow: T.cardShadow }}>
      {children}
    </div>
  )
}

function Centered({ children }) {
  const T = useQualTheme()
  return (
    <div className="min-h-full flex items-center justify-center p-6" style={{ backgroundColor: T.pageBg, color: T.text }}>
      <div className="max-w-sm w-full">{children}</div>
    </div>
  )
}
